<template>
  <div>
    <base-dialog
      :is-open="isOpen"
      max-width="900"
      @close="close"
    >
      <template #dialog-title>
        Erwartete Zahlungseingänge
      </template>
      <template #dialog-text>
        <p class="mb-2">
          Damit Zuwendungsbestätigungen erstellt werden können, müssen die untenstehenden Kontoeingänge bestätigt werden.
        </p>
        <v-alert
          v-if="error"
          type="error"
          dense
        >
          {{ error }}
        </v-alert>
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="pendingPayouts"
          :loading="loading"
          :sort-by="['date']"
          :sort-desc="[true]"
          item-key="id"
          show-select
          dense
          no-data-text="Keine ausstehenden Zahlungseingänge vorhanden"
        >
          <template #item.date="{ item }">
            <span>{{ item.date | isoToHuman('L') }}</span>
          </template>
          <template #item.amount="{ item }">
            <span>{{ item.amount | currency(currencySymbol, false) }}</span>
          </template>
        </v-data-table>
      </template>
      <template #dialog-actions>
        <v-btn @click="close">
          Schliessen
        </v-btn>
        <v-btn
          color="primary"
          :disabled="selected.length === 0"
          @click="isConfirmOpen = true"
        >
          Zahlungseingang bestätigen
        </v-btn>
      </template>
    </base-dialog>

    <base-dialog
      :is-open="isConfirmOpen"
      max-width="600"
      :persistent="uploading"
      @close="closeConfirm"
    >
      <template #dialog-title>
        Zahlungseingang bestätigen
      </template>
      <template #dialog-text>
        <p>
          Sie bestätigen den Zahlungseingang für folgende {{ selected.length }} Position(en):
        </p>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Erwarteter Eingang</th>
              <th class="text-right">
                Betrag
              </th>
              <th>Referenz</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="payout in selected"
              :key="payout.id"
            >
              <td>{{ payout.date | isoToHuman('L') }}</td>
              <td class="text-right">
                {{ payout.amount | currency(currencySymbol, false) }}
              </td>
              <td>{{ payout.sub_identifier }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <strong>Gesamt</strong>
              </td>
              <td class="text-right">
                <strong>{{ selectedTotal | currency(currencySymbol, false) }}</strong>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </v-simple-table>
        <v-alert
          v-if="uploadError"
          type="error"
          dense
          class="mt-4"
        >
          {{ uploadError }}
        </v-alert>
      </template>
      <template #dialog-actions>
        <v-btn
          :disabled="uploading"
          @click="closeConfirm"
        >
          Abbrechen
        </v-btn>
        <v-btn
          color="primary"
          :loading="uploading"
          @click="confirmUpload"
        >
          Bestätigen
        </v-btn>
      </template>
    </base-dialog>
  </div>
</template>

<script>
import BaseDialog from '@/components/UI/BaseDialog.vue'
import { SET_SNACKBAR_MESSAGE } from '@/modules/common/store/mutation-types'

const LIST_ERROR_MESSAGE = 'Zahlungseingänge konnten nicht geladen werden.'
const UPLOAD_ERROR_MESSAGE = 'Die Bestätigung konnte nicht übertragen werden. Bitte versuchen Sie es erneut.'

export default {
  name: 'PayoutsDialog',
  components: { BaseDialog },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    organization: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  data () {
    return {
      headers: [
        { text: 'Erwarteter Eingang', value: 'date' },
        { text: 'Betrag', value: 'amount', align: 'right' },
        { text: 'Referenz', value: 'sub_identifier' }
      ],
      payouts: [],
      loading: false,
      error: null,
      selected: [],
      isConfirmOpen: false,
      uploading: false,
      uploadError: null
    }
  },
  computed: {
    currencySymbol () {
      return this.organization?.currency || '€'
    },
    pendingPayouts () {
      return this.payouts.filter(payout => !payout.confirmed_received)
    },
    selectedTotal () {
      return this.selected.reduce((sum, payout) => sum + parseFloat(payout.amount), 0)
    }
  },
  watch: {
    isOpen (val) {
      if (val) this.init()
    }
  },
  methods: {
    async init () {
      this.selected = []
      this.error = null
      this.uploadError = null
      this.isConfirmOpen = false
      await this.fetchPayouts()
    },
    async fetchPayouts () {
      this.loading = true
      this.error = null
      try {
        const response = await this.$store.getters.restApi.get(`${this.organization.url}payouts/`)
        this.payouts = response.data
      } catch (e) {
        this.error = LIST_ERROR_MESSAGE
      } finally {
        this.loading = false
      }
    },
    buildCsv () {
      return this.selected
        .map(payout => `${payout.amount.replace('.', ',')};;;;${payout.id}`)
        .join('\n')
    },
    async confirmUpload () {
      this.uploading = true
      this.uploadError = null
      try {
        const blob = new Blob([this.buildCsv()], { type: 'text/csv' })
        const formData = new FormData()
        formData.append('file', blob, `zahlungseingang_${Date.now()}.csv`)
        formData.append('organization', this.organization.url)

        await this.$store.getters.restApi.post('billing/reconciliation/external_payment_confirmation/', formData)

        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: 'Zahlungseingang wurde bestätigt.',
          color: 'success'
        })
        this.isConfirmOpen = false
        this.selected = []
        await this.fetchPayouts()
      } catch (e) {
        this.uploadError = UPLOAD_ERROR_MESSAGE
      } finally {
        this.uploading = false
      }
    },
    closeConfirm () {
      if (this.uploading) return
      this.isConfirmOpen = false
      this.uploadError = null
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>
